import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ContentCategory,
  SiteContentWithCategory,
  CreateContentCategoryInput,
  UpdateContentCategoryInput,
  CreateSiteContentInput,
  UpdateSiteContentInput,
  ContentQueryInput,
  BulkUpdateContentInput
} from '@repo/types';
import { siteContentService } from '../../../lib/api/services/site-content.service';

// =============================================
// QUERY KEYS
// =============================================

export const SITE_CONTENT_KEYS = {
  all: ['site-content'] as const,

  // Public content
  public: () => [...SITE_CONTENT_KEYS.all, 'public'] as const,

  // Categories
  categories: () => [...SITE_CONTENT_KEYS.all, 'categories'] as const,
  category: (id: string) => [...SITE_CONTENT_KEYS.categories(), id] as const,

  // Content
  content: () => [...SITE_CONTENT_KEYS.all, 'content'] as const,
  contentList: (filters: ContentQueryInput) => [...SITE_CONTENT_KEYS.content(), 'list', filters] as const,
  contentItem: (id: string) => [...SITE_CONTENT_KEYS.content(), 'item', id] as const,

  // History
  history: (contentId: string) => [...SITE_CONTENT_KEYS.content(), contentId, 'history'] as const,

  // Validation
  validation: (contentId: string, value: string) =>
    [...SITE_CONTENT_KEYS.content(), contentId, 'validation', value] as const,
};

// =============================================
// PUBLIC CONTENT HOOKS (for public website)
// =============================================

export function usePublicSiteContent() {
  return useQuery({
    queryKey: SITE_CONTENT_KEYS.public(),
    queryFn: () => siteContentService.getPublicContent(),
    staleTime: 5 * 60 * 1000, // Consider fresh for 5 minutes
    //cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });
}

// =============================================
// CONTENT CATEGORIES HOOKS
// =============================================

export function useContentCategories() {
  return useQuery({
    queryKey: SITE_CONTENT_KEYS.categories(),
    queryFn: () => siteContentService.getCategories(),
  });
}

export function useContentCategory(id: string, enabled = true) {
  return useQuery({
    queryKey: SITE_CONTENT_KEYS.category(id),
    queryFn: () => siteContentService.getCategoryById(id),
    enabled: enabled && !!id,
  });
}

export function useCreateContentCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateContentCategoryInput) =>
      siteContentService.createCategory(data),
    onSuccess: (newCategory) => {
      // Invalidate categories list
      queryClient.invalidateQueries({ queryKey: SITE_CONTENT_KEYS.categories() });

      // Add the new category to cache
      queryClient.setQueryData(SITE_CONTENT_KEYS.category(newCategory.id), newCategory);
    },
  });
}

export function useUpdateContentCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateContentCategoryInput }) =>
      siteContentService.updateCategory(id, data),
    onSuccess: (updatedCategory) => {
      // Update specific category in cache
      queryClient.setQueryData(SITE_CONTENT_KEYS.category(updatedCategory.id), updatedCategory);

      // Invalidate categories list
      queryClient.invalidateQueries({ queryKey: SITE_CONTENT_KEYS.categories() });
    },
  });
}

export function useDeleteContentCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => siteContentService.deleteCategory(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: SITE_CONTENT_KEYS.category(deletedId) });

      // Invalidate categories list
      queryClient.invalidateQueries({ queryKey: SITE_CONTENT_KEYS.categories() });

      // Invalidate content list as it might be affected
      queryClient.invalidateQueries({ queryKey: SITE_CONTENT_KEYS.content() });
    },
  });
}

// =============================================
// SITE CONTENT HOOKS
// =============================================

export function useSiteContent(query: ContentQueryInput = {} as any) {
  return useQuery({
    queryKey: SITE_CONTENT_KEYS.contentList(query),
    queryFn: () => siteContentService.getContent(query),
  });
}

export function useSiteContentItem(id: string, enabled = true) {
  return useQuery({
    queryKey: SITE_CONTENT_KEYS.contentItem(id),
    queryFn: () => siteContentService.getContentById(id),
    enabled: enabled && !!id,
  });
}

export function useCreateSiteContent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSiteContentInput) =>
      siteContentService.createContent(data),
    onSuccess: (newContent) => {
      // Add to cache
      queryClient.setQueryData(SITE_CONTENT_KEYS.contentItem(newContent.id), newContent);

      // Invalidate content lists
      queryClient.invalidateQueries({ queryKey: SITE_CONTENT_KEYS.content() });

      // Invalidate public content
      queryClient.invalidateQueries({ queryKey: SITE_CONTENT_KEYS.public() });
    },
  });
}

export function useUpdateSiteContent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSiteContentInput }) =>
      siteContentService.updateContent(id, data),
    onSuccess: (updatedContent) => {
      // Update specific item in cache
      queryClient.setQueryData(SITE_CONTENT_KEYS.contentItem(updatedContent.id), updatedContent);

      // Invalidate content lists
      queryClient.invalidateQueries({ queryKey: SITE_CONTENT_KEYS.content() });

      // Invalidate public content
      queryClient.invalidateQueries({ queryKey: SITE_CONTENT_KEYS.public() });
    },
  });
}

export function useDeleteSiteContent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => siteContentService.deleteContent(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: SITE_CONTENT_KEYS.contentItem(deletedId) });

      // Invalidate content lists
      queryClient.invalidateQueries({ queryKey: SITE_CONTENT_KEYS.content() });

      // Invalidate public content
      queryClient.invalidateQueries({ queryKey: SITE_CONTENT_KEYS.public() });
    },
  });
}

// =============================================
// BULK OPERATIONS
// =============================================

export function useBulkUpdateSiteContent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BulkUpdateContentInput) =>
      siteContentService.bulkUpdateContent(data),
    onSuccess: () => {
      // Invalidate all content-related queries
      queryClient.invalidateQueries({ queryKey: SITE_CONTENT_KEYS.content() });
      queryClient.invalidateQueries({ queryKey: SITE_CONTENT_KEYS.public() });
    },
  });
}

// =============================================
// CONTENT HISTORY HOOKS
// =============================================

export function useContentHistory(contentId: string, page = 1, limit = 20, enabled = true) {
  return useQuery({
    queryKey: [...SITE_CONTENT_KEYS.history(contentId), page, limit],
    queryFn: () => siteContentService.getContentHistory(contentId, page, limit),
    enabled: enabled && !!contentId,
  });
}

// =============================================
// VALIDATION HOOKS
// =============================================

export function useValidateContent() {
  return useMutation({
    mutationFn: ({ contentId, value }: { contentId: string; value: string }) =>
      siteContentService.validateContent(contentId, value),
  });
}

// =============================================
// LEGACY COMPATIBILITY HOOKS
// =============================================

/**
 * @deprecated Use usePublicSiteContent() instead
 */
export function useSiteContentLegacy() {
  return useQuery({
    queryKey: [...SITE_CONTENT_KEYS.all, 'legacy'],
    queryFn: () => siteContentService.getAll(),
  });
}

// =============================================
// UTILITY HOOKS
// =============================================

// Hook to prefetch content for better UX
export function usePrefetchSiteContent() {
  const queryClient = useQueryClient();

  return {
    prefetchCategories: () => {
      queryClient.prefetchQuery({
        queryKey: SITE_CONTENT_KEYS.categories(),
        queryFn: () => siteContentService.getCategories(),
      });
    },
    prefetchContent: (query: ContentQueryInput = {} as any) => {
      queryClient.prefetchQuery({
        queryKey: SITE_CONTENT_KEYS.contentList(query),
        queryFn: () => siteContentService.getContent(query),
      });
    },
    prefetchPublicContent: () => {
      queryClient.prefetchQuery({
        queryKey: SITE_CONTENT_KEYS.public(),
        queryFn: () => siteContentService.getPublicContent(),
      });
    },
  };
}

// Hook to get optimistic update helpers
export function useOptimisticSiteContent() {
  const queryClient = useQueryClient();

  return {
    updateContentOptimistically: (id: string, newData: Partial<SiteContentWithCategory>) => {
      queryClient.setQueryData(
        SITE_CONTENT_KEYS.contentItem(id),
        (old: SiteContentWithCategory | undefined) =>
          old ? { ...old, ...newData } : undefined
      );
    },
    updateCategoryOptimistically: (id: string, newData: Partial<ContentCategory>) => {
      queryClient.setQueryData(
        SITE_CONTENT_KEYS.category(id),
        (old: ContentCategory | undefined) =>
          old ? { ...old, ...newData } : undefined
      );
    },
  };
}
